import { useAuth0 } from "@auth0/auth0-react";
import { useCallback } from "react";
import { UnknownRecord } from "type-fest";
import { UnknownArrayOrTuple } from "type-fest/source/internal";
import { getConfig } from "../config";
import { ApiError } from "./error";

export enum AuthenticationError {
	LOGIN_REQUIRED = "login_required",
	CONSENT_REQUIRED = "consent_required",
	MISSING_OR_EXPIRED_TOKEN = "missing_or_expired_token",
	INVALID_GRANT = "invalid_grant",
	MISSING_REFRESH_TOKEN = "missing_refresh_token",
	TIMEOUT = "timeout",
	UNKNOWN = "unknown",
}

const knownAuthenticationErrors: ReadonlySet<string> = new Set([
	AuthenticationError.LOGIN_REQUIRED,
	AuthenticationError.CONSENT_REQUIRED,
	AuthenticationError.MISSING_OR_EXPIRED_TOKEN,
	AuthenticationError.INVALID_GRANT,
	AuthenticationError.MISSING_REFRESH_TOKEN,
	AuthenticationError.TIMEOUT,
]);

export function isKnownAuthenticationError(error: unknown): error is { error: string } {
	if (typeof error !== "object" || error === null || !("error" in error)) return false;
	return typeof error.error === "string" && knownAuthenticationErrors.has(error.error);
}

export function isTerminalAuthenticationError(error: unknown): boolean {
	return isKnownAuthenticationError(error) && error.error !== AuthenticationError.TIMEOUT;
}

export const useAuthHeader = () => {
	const { getAccessTokenSilently } = useAuth0();

	const getAuthHeader = useCallback(async () => {
		try {
			return `Bearer ${await getAccessTokenSilently()}`;
		} catch (error: unknown) {
			if (isKnownAuthenticationError(error)) return undefined;
			throw error;
		}
	}, [getAccessTokenSilently]);

	return { getAuthHeader };
};

type RequestHeaders = Record<string, string>;

type UseRequestFn = {
	<T>(
		url: string,
		body?: UnknownRecord | UnknownArrayOrTuple | FormData,
		responseType?: "json",
		signal?: AbortSignal,
		headers?: RequestHeaders,
	): Promise<T>;
	<T>(
		url: string,
		body?: UnknownRecord | UnknownArrayOrTuple | FormData,
		responseType?: "text",
		signal?: AbortSignal,
		headers?: RequestHeaders,
	): Promise<string>;
	<T>(
		url: string,
		body?: UnknownRecord | UnknownArrayOrTuple | FormData,
		responseType?: "response",
		signal?: AbortSignal,
		headers?: RequestHeaders,
	): Promise<Response>;
	<T>(
		url: string,
		body?: UnknownRecord | UnknownArrayOrTuple | FormData,
		responseType?: "none",
		signal?: AbortSignal,
		headers?: RequestHeaders,
	): Promise<void>;
	<T>(
		url: string,
		body?: UnknownRecord | UnknownArrayOrTuple | FormData,
		responseType?: "blob",
		signal?: AbortSignal,
		headers?: RequestHeaders,
	): Promise<Blob>;
	<T>(
		url: string,
		body?: UnknownRecord | UnknownArrayOrTuple | FormData,
		responseType?: "json" | "text" | "response" | "none" | "blob",
		signal?: AbortSignal,
		headers?: RequestHeaders,
	): Promise<T | string | Response | void | Blob>;
};

const useRequestFn = (method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE") => {
	const { getAuthHeader } = useAuthHeader();

	const requestFn: UseRequestFn = async <T>(
		url: string,
		body?: UnknownRecord | UnknownArrayOrTuple | FormData,
		responseType: "json" | "text" | "response" | "none" | "blob" = "json",
		signal?: AbortSignal,
		headers?: RequestHeaders,
	) => {
		const authHeader = await getAuthHeader();
		if (authHeader !== undefined) {
			try {
				const requestHeaders: RequestHeaders = {
					...headers,
					Authorization: authHeader,
				};
				if (body !== undefined && !(body instanceof FormData)) {
					requestHeaders["Content-Type"] = "application/json";
				}

				const response = await fetch(getConfig().apiUrl + url, {
					method,
					mode: "cors",
					headers: requestHeaders,
					body: (() => {
						if (body instanceof FormData) return body;
						return JSON.stringify(body);
					})(),
					signal,
				});
				if (!response.ok) {
					const responseErr = await response.json();
					throw new ApiError(responseErr, { url, method, body });
				}
				if (responseType === "json") {
					const text = await response.text();
					return (text.length === 0 ? null : JSON.parse(text)) as T;
				}
				if (responseType === "text") return await response.text();
				if (responseType === "blob") return await response.blob();
				if (responseType === "response") return response;
			} catch (error) {
				throw error;
			}
		} else {
			throw new ApiError(
				{
					name: "Authentication Error",
					message: "Could not get authentication token",
					statusCode: 401,
					path: url,
					timestamp: new Date().toISOString(),
				},
				{ url, method },
			);
		}
	};

	return requestFn;
};

export const useRequest = () => ({
	get: useRequestFn("GET"),
	post: useRequestFn("POST"),
	put: useRequestFn("PUT"),
	patch: useRequestFn("PATCH"),
	_delete: useRequestFn("DELETE"),
});
